import { t } from 'i18next';
import { EmptyObject } from 'matrix-js-sdk/src';
import { ClientEvent } from 'matrix-js-sdk/src/client';
import { MatrixEvent } from 'matrix-js-sdk/src/models/event';
import { createContext, useContext, useState, PropsWithChildren, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { CalendarItemProps } from 'modules/CalendarView/CalendarSync/CalendarSyncDropdown/CalendarDropdown/CalendarItem';
import { CalendarType } from 'modules/CalendarView/CalendarSync/CalendarSyncDropdown/EditCalendarItem/CalendarRow';
import useUpdateCalendars, {
  MainCalendarInfo,
} from 'modules/CalendarView/CalendarSync/CalendarSyncDropdown/EditCalendarItem/useUpdateCalendars';
import { getCalendarItemStatus } from 'modules/CalendarView/CalendarSync/CalendarSyncDropdown/utils';
import {
  getAccountCalendarsFromMatrix,
  getUniqCalendarItems,
  getUserInfo,
} from 'modules/CalendarView/CalendarSync/CalendarSyncModal/calendarHelpers';
import { storageUtils } from 'modules/CalendarView/CalendarSync/CalendarSyncModal/utils';
import { calendarSyncLogger } from 'utils/logging/scopedLogger';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';
import { SetState } from 'utils/types';

export enum CalendarInitStatus {
  INITIAL = 'initial',
  RUNNING = 'running',
  FINISHED = 'finished',
}

interface CalendarSyncContextValue {
  calendars: CalendarItemProps[];
  setCalendars: SetState<CalendarItemProps[]>;
  initializeCalendars: () => Promise<void>;
  accountCalendars: CalendarType[];
  mainCalendarInfo: MainCalendarInfo;
  resetMainCalendarInfo: () => Promise<EmptyObject> | undefined;
  setMainCalendarInfo: (info: MainCalendarInfo) => Promise<void>;
  updateCalendarItem: (checked: boolean, id: string) => Promise<void> | undefined;
  setAccountCalendarsState: SetState<CalendarType[]>;
  isAccountCalendarsLoading: boolean;
  isCalendarAccountStateReady: boolean;
}

const CalendarSyncContext = createContext<CalendarSyncContextValue | undefined>(undefined);

const CalendarSyncProvider = ({ children }: PropsWithChildren) => {
  const [calendarInitStatus, setCalendarInitStatus] = useState(CalendarInitStatus.INITIAL);
  const [calendars, setCalendars] = useState<CalendarItemProps[]>([]);
  const [isAccountCalendarsLoading, setIsAccountCalendarsLoading] = useState(false);
  const {
    accountCalendars,
    mainCalendarInfo,
    setAccountCalendarsState,
    setMainCalendarInfoState,
    resetMainCalendarInfo,
    setMainCalendarInfo,
    updateCalendarItem,
    isCalendarAccountStateReady,
  } = useUpdateCalendars();
  const { pathname } = useLocation();
  const { matrixClient } = useMatrixContext({ requireDefinedMatrixClient: false });

  const prevAccountCalendarsRef = useRef('');
  const prevMainCalendarInfoRef = useRef('');

  const initializeCalendars = useCallback(async () => {
    const tokens = storageUtils.loadTokens();
    if (!tokens.length) return;
    setIsAccountCalendarsLoading(true);
    setCalendars([]);

    await Promise.all(
      tokens.map(async (token) => {
        const provider = storageUtils.getProviderFromToken(token);

        const { data } = await getUserInfo(provider, token.access_token);
        if (!data) return;

        const { email, id } = data;

        const calendars = getAccountCalendarsFromMatrix(matrixClient) || accountCalendars;

        const { status, isPrimary } = getCalendarItemStatus({
          t,
          accountCalendars: calendars,
          mainCalendarInfo,
          profileId: id,
        });

        return setCalendars((prev) => [
          ...prev,
          {
            isSelected: isPrimary,
            provider,
            status,
            title: email,
            id,
            accessToken: token.access_token,
          },
        ]);
      })
    );
    setIsAccountCalendarsLoading(false);
  }, [accountCalendars, mainCalendarInfo, matrixClient]);

  useEffect(() => {
    if (calendarInitStatus !== CalendarInitStatus.INITIAL || pathname.includes('auth')) return;

    setCalendarInitStatus(CalendarInitStatus.RUNNING);
    initializeCalendars()
      ?.catch((error) => {
        calendarSyncLogger.error('Error in initializeCalendars', { error });
        throw error;
      })
      .finally(() => setCalendarInitStatus(CalendarInitStatus.FINISHED));
  }, [calendarInitStatus, initializeCalendars, pathname]);

  const onAccountDataChanged = useCallback(
    (event: MatrixEvent) => {
      const [eventType, eventContent] = [event.getType(), event.getContent()];
      if (![CustomMeedioMatrixEvents.CALENDAR, CustomMeedioMatrixEvents.MAIN_CALENDAR_INFO].includes(eventType)) return;

      switch (eventType) {
        case CustomMeedioMatrixEvents.CALENDAR:
          const rawAccountCalendars = (eventContent.calendars as CalendarType[]) || [];

          const accountCalendars = getUniqCalendarItems(rawAccountCalendars);
          return setAccountCalendarsState(accountCalendars);

        case CustomMeedioMatrixEvents.MAIN_CALENDAR_INFO:
          const calendarInfo = event.getContent().info as MainCalendarInfo;
          return setMainCalendarInfoState(calendarInfo);
        default:
          return;
      }
    },
    [setAccountCalendarsState, setMainCalendarInfoState]
  );

  useEffect(() => {
    matrixClient?.on(ClientEvent.AccountData, onAccountDataChanged);
    return () => {
      matrixClient?.off(ClientEvent.AccountData, onAccountDataChanged);
    };
  }, [accountCalendars, matrixClient, onAccountDataChanged, setCalendars]);

  // Update calendar statuses when accountCalendars or mainCalendarInfo changes
  useEffect(() => {
    if (calendarInitStatus !== CalendarInitStatus.FINISHED) return;

    // Check if values have actually changed
    const currentAccountCalendars = JSON.stringify(accountCalendars);
    const currentMainCalendarInfo = JSON.stringify(mainCalendarInfo);

    if (
      prevAccountCalendarsRef.current === currentAccountCalendars &&
      prevMainCalendarInfoRef.current === currentMainCalendarInfo
    ) {
      return; // No changes, skip update
    }

    // Update refs with current values
    prevAccountCalendarsRef.current = currentAccountCalendars;
    prevMainCalendarInfoRef.current = currentMainCalendarInfo;

    setCalendars((prevCalendars) =>
      prevCalendars.map((calendar) => {
        const { status, isPrimary } = getCalendarItemStatus({
          t,
          accountCalendars,
          mainCalendarInfo,
          profileId: calendar.id,
        });

        return {
          ...calendar,
          isSelected: isPrimary,
          status,
        };
      })
    );
  }, [accountCalendars, calendarInitStatus, mainCalendarInfo, setCalendars]);

  const value: CalendarSyncContextValue = {
    setCalendars,
    calendars,
    initializeCalendars,
    isAccountCalendarsLoading,
    accountCalendars,
    mainCalendarInfo,
    resetMainCalendarInfo,
    setMainCalendarInfo,
    updateCalendarItem,
    setAccountCalendarsState,
    isCalendarAccountStateReady,
  };

  return <CalendarSyncContext.Provider value={value}>{children}</CalendarSyncContext.Provider>;
};

export const useCalendarSyncContext = (): CalendarSyncContextValue => {
  const context = useContext(CalendarSyncContext);
  if (!context) throw new Error('useCalendarSyncContext must be used within a CalendarSyncProvider');

  return context;
};

export default CalendarSyncProvider;
